import type { MapEntry } from "@qendo/util";
import { ucfirst } from "@samlevy/string-helpers";
import { type Dispatch, type ReactElement, type SetStateAction, useCallback, useMemo, useState } from "react";
import { ActivityIndicator, Platform, StatusBar, StyleSheet, View } from "react-native";
import { Dropdown } from "react-native-element-dropdown";
import { Dialog } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Button } from "~/components/Button";
import { FieldLabel, type FieldLabelProps } from "~/components/form/FieldLabel";
import { CentredModal } from "~/components/layout/modal/CentredModal";
import { BaseText, Subtitle } from "~/components/layout/text";
import { Only } from "~/components/permission/Only";
import { Gap, Margin, Padding, Rounded } from "~/constants/layout";
import { INPUT_HEIGHT, Radius, Space } from "~/constants/sizing";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { setWidthOnLayout } from "~/util/layout";
import { QendoIcon } from "../../QendoIcon";

const statusBarHeight: number = StatusBar.currentHeight || 0;
type ComboMode = "default" | "modal" | "auto";

export type ItemType<T> = {
    label?: string;
    subtitle?: string;
    value?: T;
    icon?: () => ReactElement;
    custom?: boolean;
    group?: string | null;
};
export type ValueLike = number | string;
export type ItemLike<TValue extends ValueLike = ValueLike> =
    | TValue
    | ItemType<TValue>
    | [string, TValue?]
    | MapEntry<TValue, string | ItemType<TValue> | undefined>
    | object;
export type ItemMapFunction<
    TValue extends ValueLike,
    TInput extends ItemLike<TValue> = ItemLike<TValue>,
    TItem extends ItemType<TValue> = ItemType<TValue>,
> = (item: TInput, index: number, array: ItemLike<TValue>[]) => TItem;

type NullableComboProps<TValue extends ValueLike> = {
    value?: TValue | null;
    onItemSelected: ((v: TValue | null) => void) | Dispatch<SetStateAction<TValue | null>>;
    clearable?: boolean;
};

type FilledComboProps<TValue extends ValueLike> = {
    value: TValue | null;
    onItemSelected: ((v: TValue) => void) | Dispatch<SetStateAction<TValue>>;
    clearable?: false | never;
};

type BaseComboProps<
    TValue extends ValueLike,
    TInput extends ItemLike<TValue> = ItemLike<TValue>,
    TItem extends ItemType<TValue> = ItemType<TValue>,
> = {
    name?: string;
    value?: TValue | null;
    onItemSelected:
        | ((v: TValue | null) => void)
        | Dispatch<SetStateAction<TValue | null>>
        | ((v: TValue) => void)
        | Dispatch<SetStateAction<TValue>>;
    items: TInput[];
    mapItems?: ItemMapFunction<TValue, TInput, TItem>;
    itemMap?: Record<TValue, string | TItem>;
    disabled?: boolean;
    searchable?: boolean;
    addable?: boolean;
    onAddItem?: (value: TValue, group?: string | null) => Promise<TValue | null>;
    zIndex?: number;
    placeholder?: string;
    clearable?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    searchField?: "label" | "value";
    mode?: ComboMode;
} & (NullableComboProps<TValue> | FilledComboProps<TValue>) &
    FieldLabelProps;

export type GroupComboProps<
    TValue extends ValueLike = string,
    TInput extends ItemLike<TValue> = ItemLike<TValue>,
    TItem extends ItemType<TValue> = ItemType<TValue>,
> = {
    addable?: false;
    clearable?: false;
    onAddItem?: never;
    onItemSelected?: never;
} & Omit<BaseComboProps<TValue, TInput, TItem>, "addable" | "clearable" | "onAddItem" | "onItemSelected">;

export type ComboBoxProps<
    TValue extends ValueLike,
    TInput extends ItemLike<TValue> = ItemLike<TValue>,
    TItem extends ItemType<TValue> = ItemType<TValue>,
    TGroups extends GroupComboProps = GroupComboProps,
> = {
    groups?: TGroups | null;
} & BaseComboProps<TValue, TInput, TItem>;

const basicMap: ItemMapFunction<ValueLike> = (item) => {
    return makeItem(item);
};

const makeItem = <TValue extends ValueLike, TItem extends ItemType<TValue>>(
    item: ItemLike<TValue>,
    label?: string,
): TItem => {
    if (Array.isArray(item)) {
        const [v, l] = item;

        return makeItem(v, `${l ?? label ?? v}`);
    }

    if (typeof item === "object") {
        if (Object.hasOwn(item, "value")) {
            return {
                label: label ?? `${(item as ItemType<TValue>).label ?? (item as ItemType<TValue>).value}`,
                ...item,
            } as TItem;
        } else if (label && Object.hasOwn(item, label)) {
            return makeItem(label as TValue, (item as Record<TValue, string>)[label as TValue]);
        }

        return item as TItem;
    }

    return { value: item as TValue, label: `${label ?? ucfirst(item as string)}` } as TItem;
};

export function ComboBox<
    TValue extends ValueLike = ValueLike,
    TInput extends ItemLike<TValue> = TValue,
    TItem extends ItemType<TValue> = ItemType<TValue>,
    TGroups extends GroupComboProps = GroupComboProps,
>({
    value,
    name = "_none",
    onItemSelected,
    items,
    mapItems,
    itemMap,
    disabled,
    searchable,
    addable,
    onAddItem,
    placeholder,
    zIndex,
    clearable,
    error,
    labelContainerStyle,
    onFocus,
    onBlur,
    groups,
    mode,
    searchField = "label",
    ...labelProps
}: ComboBoxProps<TValue, TInput, TItem, TGroups>) {
    const { inputs, colours, background, border } = useTheme();
    const errorBag = useErrorBag();
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth);
    const [open, setOpen] = useState<boolean>(false);
    const [search, setSearch] = useState("");
    const [group, setGroup] = useState<string | null>(null);
    const [adding, setAdding] = useState<ItemType<TValue> | null>(null);
    const [saving, setSaving] = useState(false);
    const { top, bottom } = useSafeAreaInsets();

    const handleFocus = () => {
        setOpen(true);
        onFocus?.();
    };

    const handleBlur = () => {
        setOpen(false);
        onBlur?.();
    };

    const mapper: ItemMapFunction<TValue, TInput, TItem> = useCallback(
        (item, index, array) => {
            const value =
                itemMap && typeof item !== "object"
                    ? typeof itemMap[item as TValue] === "string"
                        ? (makeItem(item, itemMap[item as TValue] as string) as TItem)
                        : makeItem(itemMap[item as TValue] as ItemLike<TValue>)
                    : item;

            return (mapItems ?? basicMap)(value as TInput, index, array) as TItem;
        },
        [mapItems, itemMap],
    );

    const options = useMemo(() => items.map(mapper) as ItemType<TValue>[], [items, mapper]);
    const selected = useMemo(() => options.find((v) => v.value === value) ?? null, [options, value]);

    const all_options = useMemo(() => {
        if (
            !addable ||
            search.trim().length === 0 ||
            options.find((o) => o.label?.toLowerCase() === search.trim().toLowerCase())
        ) {
            return options;
        }

        return [
            ...options,
            {
                value: search,
                label: search,
                custom: true,
            },
        ];
    }, [options, addable, search]);

    const cancelAdding = () => {
        setAdding(null);
        setGroup(null);
        if (groups?.name) {
            errorBag.forget(groups.name);
        }
    };

    const itemSelected = (item: ItemType<TValue> | null) => {
        const selected_value = item?.value ?? null;

        errorBag.forget(name);

        if (item && Object.hasOwn(item, "custom")) {
            if (addable && selected_value && onAddItem) {
                if (groups && !item.group) {
                    setAdding(item);
                } else {
                    setSaving(true);

                    onAddItem((item.label ?? selected_value) as TValue, item.group)
                        .then((created) => created && onItemSelected(created))
                        .then(cancelAdding)
                        .catch(() => onItemSelected(value as TValue))
                        .finally(() => setSaving(false));
                }
            } else {
                onItemSelected(value as TValue);
            }
        } else {
            if (clearable && value && selected_value === value) {
                onItemSelected(null);
            } else {
                onItemSelected(selected_value as TValue);
            }
        }
    };

    const selectGroup = () => {
        if (adding && groups && group) {
            itemSelected({ ...adding, group });
        }
    };

    const field_error = error ?? errorBag.first(name);

    // biome-ignore lint/correctness/useExhaustiveDependencies(options.length): Should not change display method
    const dropdown_mode = useMemo(
        () => mode ?? (searchable || addable || (searchable !== false && options.length >= 10) ? "modal" : undefined),
        [mode, searchable, addable],
    );

    return (
        <>
            <FieldLabel
                {...labelProps}
                accessibilityRole="combobox"
                error={field_error}
                style={[labelProps.style, { zIndex: open ? (zIndex ?? 5000) : 0 }]}
            >
                <View
                    style={[
                        inputs.input,
                        styles.container,
                        Boolean(field_error) && inputs.error,
                        open && inputs.focus,
                        disabled && inputs.disabled,
                        labelContainerStyle,
                    ]}
                    onLayout={onLayout}
                >
                    <Dropdown
                        data={all_options}
                        mode={dropdown_mode}
                        valueField="value"
                        labelField="label"
                        onChange={itemSelected}
                        value={selected}
                        itemTextStyle={inputs.inputText}
                        inputSearchStyle={[background.secondary, Rounded.md]}
                        selectedTextStyle={[inputs.inputText, Margin.horizontal_xxs]}
                        iconColor={colours.icon.secondary}
                        activeColor={colours.background["secondary-pressed"]}
                        searchPlaceholderTextColor={colours.text.tertiary}
                        style={[inputs.input, styles.picker, Gap.none, { width: width - 4 }]}
                        containerStyle={[
                            background.primary,
                            border.primary,
                            inputs.shadow,
                            styles.dropdownContainerStyle,
                            { marginBottom: bottom + Space.md },
                            dropdown_mode === "modal" && { marginTop: top + Space.md },
                        ]}
                        placeholderStyle={inputs.placeholder}
                        search={searchable || addable || (searchable !== false && options.length >= 10)}
                        searchField={searchField}
                        searchPlaceholder={addable ? "Search or add..." : "Search..."}
                        onChangeText={setSearch}
                        disable={disabled}
                        placeholder={placeholder}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        renderRightIcon={() => (
                            <>
                                {saving && <ActivityIndicator size={20} />}
                                {clearable && value && (
                                    <Button
                                        icon="xmark"
                                        size="sm"
                                        variant="neutral-quiet"
                                        disabled={disabled}
                                        onPress={() => itemSelected(null)}
                                    />
                                )}
                                <QendoIcon name={open ? "chevron-up" : "chevron-down"} size={16} />
                            </>
                        )}
                        renderLeftIcon={() => selected?.icon?.() ?? null}
                        renderItem={(item, selected) => (
                            <View style={[styles.item, border.bottom_secondary]}>
                                {item.icon?.()}
                                <View style={styles.itemBody}>
                                    <BaseText
                                        colour={selected ? "selected" : "primary"}
                                        decoration={item.custom ? "italic" : "normal"}
                                        numberOfLines={1}
                                    >
                                        {item.label}
                                    </BaseText>
                                    <Only when={item.subtitle}>
                                        <Subtitle>{item.subtitle}</Subtitle>
                                    </Only>
                                </View>
                                <Only when={item.custom}>
                                    <QendoIcon name="plus" size={20} color={colours.text.secondary} />
                                </Only>
                                <Only when={selected}>
                                    <QendoIcon name="checkmark-thicker" size={20} color={colours.text.selected} />
                                </Only>
                            </View>
                        )}
                    />
                </View>
            </FieldLabel>
            {adding && groups && (
                <CentredModal onDismiss={cancelAdding}>
                    <Dialog.Content style={[Padding.vertical_none]}>
                        <ComboBox {...groups} onItemSelected={setGroup} />
                    </Dialog.Content>
                    <Dialog.Actions>
                        <Button variant="neutral-quiet" title="Cancel" onPress={cancelAdding} />
                        <Button
                            variant="primary"
                            title="Add"
                            disabled={!group}
                            onPress={selectGroup}
                            loading={saving}
                        />
                    </Dialog.Actions>
                </CentredModal>
            )}
        </>
    );
}

const styles = StyleSheet.create({
    container: {
        flexDirection: "column",
        paddingVertical: 0,
        paddingHorizontal: 0,
    },
    picker: {
        borderWidth: 0,
        height: INPUT_HEIGHT - 4,
    },
    dropdownContainerStyle: {
        borderRadius: Radius.lg,
        paddingBottom: Space.xxs,
        paddingTop: Space.xxx,
        marginTop: Platform.select({
            native: -statusBarHeight,
            default: undefined,
        }),
    },
    item: {
        flexDirection: "row",
        gap: Space.sm,
        alignItems: "center",
        padding: Space.sm,
        borderBottomWidth: 1,
    },
    itemBody: {
        flexGrow: 1,
        gap: Space.xxx,
        justifyContent: "center",
    },
});
